import { z } from 'zod';
import { ChatActionDraft, ChatTaskScope } from './chat-actions.js';
import { Role } from './enums.js';
import { IsoDateTime, UuidString } from './primitives.js';

/**
 * S7 — Excel publisher / override detector contracts.
 *
 * ExcelSnapshot   — metadata опубликованного workbook (S3/MinIO key).
 * OverrideDiff    — результат override-detector (diff uploaded vs last snapshot).
 * ChangedCell     — одна измененная cell.
 * OverrideDecision — accept | defer | reject для ревью изменений.
 */

export const ExcelSheetName = z.enum([
  'README_CONTROL',
  'RUN_SUMMARY',
  'INVENTORY_SNAPSHOT',
  'WRITEOFF_ALLOCATIONS',
  'COST_ALLOCATIONS',
  'SOURCE_CONFLICTS',
  'ANOMALIES_REVIEW',
  'EVIDENCE_INDEX',
]);
export type ExcelSheetName = z.infer<typeof ExcelSheetName>;

export const ExcelSemanticField = z.enum(['qty', 'valuation_rate', 'note', 'status', 'other']);
export type ExcelSemanticField = z.infer<typeof ExcelSemanticField>;

export const OverrideDecisionValue = z.enum(['pending', 'accept', 'defer', 'reject']);
export type OverrideDecisionValue = z.infer<typeof OverrideDecisionValue>;

export const ChangedCell = z.object({
  /** Alias для `change_id` в UI-контексте. */
  id: UuidString.optional(),
  change_id: UuidString,
  sheet: ExcelSheetName,
  cell: z.string(), // A1 notation
  before: z.unknown(),
  after: z.unknown(),
  was_locked: z.boolean(),
  semantic_field: ExcelSemanticField.optional(),
  /** Pending decision на стороне UI; backend возвращает без поля до ревью. */
  decision: z.enum(['pending', 'accept', 'defer', 'reject']).optional(),
});
export type ChangedCell = z.infer<typeof ChangedCell>;

export const OverrideDiff = z.object({
  /** override_id — UI-контекст; backend возвращает upload_id. */
  id: UuidString.optional(),
  snapshot_id: UuidString,
  upload_id: UuidString,
  status: z.enum(['pending_review', 'finalized', 'rejected']).optional(),
  changed_cells: z.array(ChangedCell),
  summary: z.object({
    total: z.number().int().nonnegative(),
    locked: z.number().int().nonnegative(),
    editable: z.number().int().nonnegative(),
    total_changes: z.number().int().nonnegative().optional(),
    locked_changes: z.number().int().nonnegative().optional(),
    by_sheet: z.record(z.number().int().nonnegative()).optional(),
    by_decision: z.record(z.number().int().nonnegative()).optional(),
  }),
});
export type OverrideDiff = z.infer<typeof OverrideDiff>;

export const OverrideDecideInput = z
  .object({
    decisions: z
      .array(
        z
          .object({
            change_id: UuidString,
            decision: z.enum(['accept', 'defer', 'reject']),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();
export type OverrideDecideInput = z.infer<typeof OverrideDecideInput>;

export const ExcelSnapshotRow = z
  .object({
    id: UuidString,
    period: z.string(), // 'YYYY-MM'
    s3_key: z.string(),
    sha256: z.string(),
    schema_version: z.string(),
    created_by_user_id: UuidString,
    created_at: IsoDateTime,
    run_id: UuidString.nullable(),
  })
  .strict();
export type ExcelSnapshotRow = z.infer<typeof ExcelSnapshotRow>;

export const ExcelPublishRequest = z
  .object({
    period: z.string().regex(/^\d{4}-\d{2}$/),
    run_id: UuidString.optional(),
    approver_user_id: UuidString.optional(),
  })
  .strict();
export type ExcelPublishRequest = z.infer<typeof ExcelPublishRequest>;

// ─── Chat NLQ ─────────────────────────────────────────────────────────

export const ChatRole = z.enum(['user', 'assistant', 'tool', 'system']);
export type ChatRole = z.infer<typeof ChatRole>;

/**
 * chat-interactive-actions B2 [Б14] — `scope` остаётся открытой картой (item_id /
 * bom_id / work_order_id / stock_entry_id пишет `deriveChatContext`), но подключ
 * `tasks` валидируется strict-подсхемой `ChatTaskScope`: 101 выделенный id или
 * `surface:'project'` без `project_id` — 422 на границе, а не молчаливая порча
 * контекста инструментов. Тип поля намеренно не сужен до объекта: старые клиенты
 * шлют `Record<string, unknown>`, и типовой слой фронта переезжает отдельно (F6).
 */
export const ChatScope = z.record(z.unknown()).superRefine((scope, ctx) => {
  if (!('tasks' in scope) || scope['tasks'] === undefined) return;
  const parsed = ChatTaskScope.safeParse(scope['tasks']);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      ctx.addIssue({ ...issue, path: ['tasks', ...issue.path] });
    }
  }
});

export const ChatMessageInput = z
  .object({
    message: z.string().min(1).max(4000),
    conversation_id: UuidString.optional(),
    context: z
      .object({
        run_id: UuidString.optional(),
        period: z.string().optional(),
        scope: ChatScope.optional(),
        route: z.string().optional(),
        // CH-7 — role optional; backend validates match with JWT (anti-spoof).
        role: Role.optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type ChatMessageInput = z.infer<typeof ChatMessageInput>;

export const ChatMessageRow = z
  .object({
    id: UuidString,
    conversation_id: UuidString,
    role: ChatRole,
    content: z.string(),
    tool_calls: z.array(z.record(z.unknown())).optional(),
    created_at: IsoDateTime,
  })
  .strict();
export type ChatMessageRow = z.infer<typeof ChatMessageRow>;

export const ChatConversationRow = z
  .object({
    id: UuidString,
    user_id: UuidString,
    title: z.string(),
    created_at: IsoDateTime,
    last_message_at: IsoDateTime,
  })
  .strict();
export type ChatConversationRow = z.infer<typeof ChatConversationRow>;

/**
 * chat-interactive-actions B10 — контракты бесед, которых до пака не было в shared.
 *
 * `POST /chat/conversations` фронт звал с самого S7 (`lib/api/chat.ts:25`), а ручки
 * не существовало: кнопка «Новая» отвечала 404. Тело — только заголовок; если он не
 * прислан, беседа заводится без имени, и первое сообщение его перезапишет.
 */
export const ChatConversationCreateInput = z
  .object({ title: z.string().min(1).max(200).optional() })
  .strict();
export type ChatConversationCreateInput = z.infer<typeof ChatConversationCreateInput>;

export const ChatConversationsListResponse = z
  .object({ items: z.array(ChatConversationRow) })
  .strict();
export type ChatConversationsListResponse = z.infer<typeof ChatConversationsListResponse>;

/**
 * Ответ `GET /chat/conversations/:id` [П6]: карточки действий в истории читаются
 * из `drafts`, а НЕ восстанавливаются из `tool_calls` сообщения — статус карточки
 * (`proposed`/`consumed`/`cancelled`/`expired`) живёт на черновике и меняется после
 * того, как сообщение уже записано.
 */
export const ChatConversationHistoryResponse = z
  .object({
    conversation: ChatConversationRow,
    messages: z.array(ChatMessageRow),
    drafts: z.array(ChatActionDraft),
  })
  .strict();
export type ChatConversationHistoryResponse = z.infer<typeof ChatConversationHistoryResponse>;
